/// ----------------------------------------------------------------------------------------
///Copyright (C) 2016 to the present, Crestron Electronics, Inc.
///All rights reserved.
///No part of this software may be reproduced in any form, machine
///or natural, without the express written consent of Crestron Electronics.
/// ----------------------------------------------------------------------------------------
/// <file>
/// <name>HydUser.ts</name>
///
/// <description>
/// A class that models a Hydrogen user including login, authentication and authorization information
/// </description>
///
/// <author>Yaron Habot</author>
/// <created>12 Aug 2016</created>
///
/// <changelog>
///   Date  Who Version     Flag Comments
/// ======= === =========== ==== ===========================================================
/// 12Aug16 YH  1.0.0000         First cut
/// 14Jan17 YH  1.0.0000         Bug 121121 - When refreshing browser the User Portal goes blank
/// 21Mar17 YH                   Bug 124419 - [NAV]Login Allowed for a Particular Account User(Account is logged out) through URL
/// </changelog>
///
/// </file>
/// ----------------------------------------------------------------------------------------
///
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Injectable } from '@angular/core'
import { MediatorEventLookup } from './../services/mediator-event-lookup.service';
import { MediatorService } from './../services/mediator.service';

@Injectable()
export class HydUser {
    private hydUserInfo : HydUserInfo;
    private hydUserInfoSessionStorageName = "hydUserInfo";
    //YH 3/21/2017 Bug 124419
    private authenticationCodeStateStorageName : string = "authenticationCodeState";

    //Observables
    public AuthenticationObservable:Subject<boolean>;
    public AuthorizationObservable:Subject<boolean>;

    constructor(private mediator:MediatorService) {
        this.AuthenticationObservable = new Subject<boolean>();
        this.AuthorizationObservable = new Subject<boolean>();

        this.LoadHydUserInfoFromSessionStorage();
    }

    public SetAccessToken(accessToken:string) {
        this.hydUserInfo._accessToken = accessToken;
    }

    //YH 3/21/2017 Bug 124419
    //Prevent authentication code replay by storing the State and verify on auth reply that it is the same State as the login
    public setAuthenticationCodeState(state:string) {
        sessionStorage.setItem(this.authenticationCodeStateStorageName,state);
    }

    public get authenticationCodeState() : string {
        return sessionStorage.getItem(this.authenticationCodeStateStorageName);
    }

    public OnUserAuthenticated (
        givenName: string,
        lastName: string,
        roles: Array<string>,
        uniqueName: string,
        email: string,
        tokenExpiration: number,
        refreshToken: string,
        accountId: string,
        accountNumber: string,
        tier: string,
        allottedCount: number,
        accountExpiration: string,
        deviceCount: number,
        alertEnabled:boolean
    )
    {
        this.hydUserInfo._givenName = givenName;
        this.hydUserInfo._lastName = lastName;
        this.hydUserInfo._roles = roles;
        this.hydUserInfo._uniqueName = uniqueName;
        this.hydUserInfo._email = email;
        this.hydUserInfo._tokenExpiration = tokenExpiration;
        this.hydUserInfo._refreshToken = refreshToken;
        this.hydUserInfo._accountId = accountId;
        this.hydUserInfo._accountNumber = accountNumber;
        this.hydUserInfo._tier = tier;
        this.hydUserInfo._allottedCount = allottedCount;
        this.hydUserInfo._accountExpiration = accountExpiration;
        this.hydUserInfo._deviceCount = deviceCount;
        this.hydUserInfo._alertEnabled=alertEnabled;
        this.AuthenticationObservable.next(true);
    }

    //YH 12/12/2016 accessToken is set prior to user authorization. refreshToken is not used by Azure AD anymore
    // public OnUserAuthorized(accessToken,refreshToken) {
    //     this._isAuthorized = true;
    //     this._accessToken = accessToken;
    //     this._refreshToken = refreshToken;
    //     this.AuthorizationObservable.next(true);
    // }
    public OnUserAuthorized() {
        this.hydUserInfo._isAuthorized = true;
        sessionStorage.setItem(this.hydUserInfoSessionStorageName,JSON.stringify(this.hydUserInfo));
        this.AuthorizationObservable.next(true);
    }

    public OnUserNotAuthorized(errorCode,errorMsg) {
        this.hydUserInfo._isAuthorized = false;
        this.hydUserInfo._authorizationErrorCode = errorCode;
        this.hydUserInfo._authorizationErrorMsg = errorMsg;
        this.AuthorizationObservable.next(false);
    }

    public logout() {
        sessionStorage.removeItem(this.hydUserInfoSessionStorageName);
        sessionStorage.removeItem(this.authenticationCodeStateStorageName);
    }
    public get GivenName() {
        return this.hydUserInfo ? this.hydUserInfo._givenName : "";
    }

    public get LastName() {
        return this.hydUserInfo ? this.hydUserInfo._lastName : "";
    }

    public get UserId() {
        return this.hydUserInfo ? this.hydUserInfo._uniqueName : null;
    }

    public get AccountId() {
        return this.hydUserInfo ? this.hydUserInfo._accountId : null;
    }

    public get AccountNumber() {
        return this.hydUserInfo ? this.hydUserInfo._accountNumber : null;
    }

    public get AccountTier() {
        return this.hydUserInfo ? this.hydUserInfo._tier : null;
    }

    public get AllottedCount() {
        return this.hydUserInfo ? this.hydUserInfo._allottedCount : null;
    }

    public get DeviceCount() {
        return this.hydUserInfo ? this.hydUserInfo._deviceCount : null;
    }

    public get AccountExpiration() {
        return this.hydUserInfo ? this.hydUserInfo._accountExpiration : null;
    }

    public get isLoggedIn() {
        let userAuthorized : boolean = false;
        let tokenCurrent : boolean = false;
        if (this.hydUserInfo) {
            userAuthorized = this.hydUserInfo._isAuthorized;
            tokenCurrent = this.hydUserInfo._tokenExpiration*1000 >= Date.now();
        }
        return userAuthorized && tokenCurrent;
        //return this.hydUserInfo ? this.hydUserInfo._isAuthorized : null;
    }

    public get AccessToken() {
        return this.hydUserInfo ? this.hydUserInfo._accessToken : null;
    }

    public get AuthorizationErrorMsg() {
        return this.hydUserInfo ? this.hydUserInfo._authorizationErrorMsg : null;
    }
    public get Roles() : Array<string> {
        return this.hydUserInfo._roles;
    }

    public isRole(roleValue:string) : boolean {
        let role: string = this.Roles.find(role=> role === roleValue);
        let isRole = role !== undefined && role !== null && role.length>0;
        return isRole;
    }

    public get TokenExpirationEpoch() : number {
        return this.hydUserInfo._tokenExpiration;
    }

    public get RefreshToken() : string {
        return this.hydUserInfo._refreshToken;
    }

    public get AlertEnabled():boolean {
      return this.hydUserInfo ? this.hydUserInfo._alertEnabled : null;
  }


    public RefreshAccessToken(accessToken:string, refreshToken:string, expiration:number) : void {
        this.hydUserInfo._accessToken = accessToken;
        this.hydUserInfo._refreshToken = refreshToken;
        this.hydUserInfo._tokenExpiration = expiration;
        sessionStorage.setItem(this.hydUserInfoSessionStorageName,JSON.stringify(this.hydUserInfo));
    }

    //Bug 121121 - When refreshing browser the User Portal goes blank
    private LoadHydUserInfoFromSessionStorage() {
        if (typeof(Storage) !== "undefined" && sessionStorage[this.hydUserInfoSessionStorageName]) {
            this.hydUserInfo = JSON.parse(sessionStorage[this.hydUserInfoSessionStorageName]);
            //Below logic to handle hadle the manual browser refresh : reset the refresh token timer service
            let inst=this;
            setTimeout(function(){
              inst.mediator.Action(MediatorEventLookup.RefreshToken,true);
            },1000);

          }
        else {
            this.hydUserInfo = new HydUserInfo();
        }
    }
};

class HydUserInfo {
    public _givenName: string;
    public _lastName: string;
    public _roles: Array<string>;
    public _uniqueName: string;
    public _email: string;
    public _accountId: string;
    public _accountNumber: string;
    public _tier: string;
    public _allottedCount: number;
    public _deviceCount: number;
    public _accountExpiration: string;
    public _alertEnabled: boolean;

    public _accessToken: string;
    public _refreshToken: string;
    public _tokenExpiration: number;
    public _authorizationErrorCode: string;
    public _authorizationErrorMsg: string;
    public _isAuthorized: boolean;
}



// WEBPACK FOOTER //
// C:/WS/Fusion/FusionDashboard/src/app/models/user.ts